import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Clock, Calendar, Users, AlertCircle, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { fmtRange12 } from '../../utils/time';
import toast from 'react-hot-toast';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import clsx from 'clsx';

function ShiftCard({ shift, onApply }) {
  const when = isToday(parseISO(shift.shift_date)) ? 'Today'
    : isTomorrow(parseISO(shift.shift_date)) ? 'Tomorrow'
    : format(parseISO(shift.shift_date), 'EEE dd MMM');

  return (
    <div className="card p-5 hover:shadow-card-hover transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded-full', isToday(parseISO(shift.shift_date)) ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700')}>
              {when}
            </span>
            {shift.shift_type && <span className="badge-gray capitalize">{shift.shift_type.replace('_',' ')}</span>}
          </div>
          <p className="font-bold text-slate-900">
            {fmtRange12(shift.start_time, shift.end_time)}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-slate-400">Duration</p>
          <p className="font-semibold text-slate-900 text-sm">
            {(() => {
              const [sh,sm] = (shift.start_time||'').split(':').map(Number);
              const [eh,em] = (shift.end_time||'').split(':').map(Number);
              const mins = (eh*60+em) - (sh*60+sm);
              return `${(mins/60).toFixed(1)}h`;
            })()}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {shift.address && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <MapPin size={13} className="shrink-0 text-slate-400" />
            <span className="truncate">{shift.address}</span>
          </div>
        )}
        {shift.client_names && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Users size={13} className="shrink-0 text-slate-400" />
            <span className="truncate">{shift.client_names}</span>
          </div>
        )}
        {shift.instructions && (
          <div className="flex items-start gap-2 text-sm text-slate-500">
            <AlertCircle size={13} className="shrink-0 text-slate-400 mt-0.5" />
            <span className="line-clamp-2">{shift.instructions}</span>
          </div>
        )}
      </div>

      <button
        onClick={() => onApply(shift)}
        className="mt-4 w-full btn-primary justify-center"
      >
        Apply for Shift <ChevronRight size={14} />
      </button>
    </div>
  );
}

export default function JobBoard() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDate] = useState('upcoming');

  const load = () => {
    setLoading(true);
    api.get('/shifts/board/vacant')
      .then(r => setShifts(r.data))
      .catch(() => toast.error('Failed to load job board'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const applyForShift = async (shift) => {
    try {
      await api.post(`/shifts/${shift.id}/apply`);
      toast.success('Application submitted — admin will confirm');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to apply for this shift');
    }
  };

  const filtered = shifts.filter(s => {
    const d = parseISO(s.shift_date);
    if (dateFilter === 'today') return isToday(d);
    if (dateFilter === 'tomorrow') return isTomorrow(d);
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Job Board</h1>
          <p className="text-sm text-slate-400 mt-0.5">{shifts.length} open shift{shifts.length !== 1 ? 's' : ''} available</p>
        </div>
      </div>

      {shifts.length > 0 && (
        <div className="card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <Briefcase size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Shifts needing coverage</p>
            <p className="text-sm text-slate-400">Apply for available shifts below. Your manager will confirm once assigned.</p>
          </div>
        </div>
      )}

      <div className="card p-4">
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl w-fit">
          {[['upcoming','All Upcoming'],['today','Today'],['tomorrow','Tomorrow']].map(([v,l]) => (
            <button key={v} onClick={() => setDate(v)}
              className={clsx('px-3 py-1.5 text-xs font-medium rounded-lg transition-all', dateFilter === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500')}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-16 text-center">
          <Briefcase size={48} className="text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-semibold">No vacant shifts at this time</p>
          <p className="text-slate-400 text-sm mt-1">Check back later or contact your coordinator</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(s => <ShiftCard key={s.id} shift={s} onApply={applyForShift} />)}
        </div>
      )}
    </div>
  );
}
